import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable react-hooks/exhaustive-deps */
import { BackdropLoader, BasicTooltip, Icon, SimpleModal, SummaryInfo } from '@livable/saas-react-components';
import { Accordion, AccordionDetails, AccordionSummary, Button, Paper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableModal from 'src/pages/AllocationTablesPage/AllocationUtility/TableModal';
import useAllocationUtilityStyles from 'src/pages/AllocationTablesPage/AllocationUtility/styles';
import { useStore } from 'src/stores';
import { SubmeterMeterRows, SubmeterMeterRowsError } from './SubmeterMeterRows';

// A New Submeter charge is the one that carries its meters; the old interim submeter and every other charge
// arrive with none, and keep the RUBS preview.
const hasMeters = submeterMeters => Array.isArray(submeterMeters) && submeterMeters.length > 0;
const AllocationPreviewModal = _ref => {
  var _store$utilityProvide;
  let billingCycleId = _ref.billingCycleId,
    propertyId = _ref.propertyId,
    statementPropertyId = _ref.statementPropertyId,
    submeterMeters = _ref.submeterMeters,
    triggerType = _ref.triggerType,
    utility = _ref.utility;
  const classes = useAllocationUtilityStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const id = billingCycleId + propertyId + utility.utilityId;
  const queue = store.allocations.queue;
  const utilityTable = store.allocations.utilityById.get(billingCycleId + utility.utilityId);
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    modalIsOpen = _useState6[0],
    setModalIsOpen = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    meterRows = _useState8[0],
    setMeterRows = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    meterRowsFailed = _useState0[0],
    setMeterRowsFailed = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isMeterRowsLoading = _useState10[0],
    setIsMeterRowsLoading = _useState10[1];
  const meterRowsRead = useRef(0);
  const showsMeterRows = hasMeters(submeterMeters);
  // The meter rows belong to the statement's own property, which is not the page's for a statement from an
  // earlier residency at another property.
  const meterRowsPropertyId = statementPropertyId || propertyId;
  const loadMeterRows = async () => {
    meterRowsRead.current += 1;
    const read = meterRowsRead.current;
    setMeterRows(null);
    setMeterRowsFailed(false);
    setIsMeterRowsLoading(true);
    const resp = await store.allocations.getSubmeterBillingAllocationsPreviewAsync.run({
      billingCycleId,
      propertyId: meterRowsPropertyId,
      utilityId: utility.utilityId
    });
    if (read !== meterRowsRead.current) return;
    setIsMeterRowsLoading(false);
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !Array.isArray(resp.response)) {
      setMeterRowsFailed(true);
      return;
    }
    setMeterRows(resp.response);
  };
  useEffect(() => {
    if (!modalIsOpen) return;
    setIsLoading(id);
    store.allocations.addToQueue({
      billingCycleId,
      id,
      propertyId,
      utilityId: utility.utilityId
    });
    if (showsMeterRows) loadMeterRows();

    // Closing, or the charge changing under an open modal, ends this read: its answer is for a charge that is
    // no longer shown, and its rows must not outlive it.
    return () => {
      meterRowsRead.current += 1;
      setMeterRows(null);
      setMeterRowsFailed(false);
      setIsMeterRowsLoading(false);
    };
  }, [modalIsOpen, billingCycleId, propertyId, meterRowsPropertyId, utility.utilityId, showsMeterRows]);
  useEffect(() => {
    if (!queue.find(item => item.id === id)) setIsLoading(false);
  }, [queue]);
  const defaultTrigger = type => {
    return type === 'actionButton' ? /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "actionButton",
      'custom-attribute': '422-AllocationPreviewModal'
    }, "View allocation") : /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t('resident_account.view_allocation_table'),
      placement: "top"
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      size: "small",
      'custom-attribute': '423-AllocationPreviewModal'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "table"
    })));
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    handleClose: () => setModalIsOpen(false),
    handleOpen: () => setModalIsOpen(true),
    isOpen: modalIsOpen,
    noPadding: true,
    title: t('allocations.allocation_preview'),
    trigger: defaultTrigger(triggerType),
    variant: "large"
  }, /*#__PURE__*/React.createElement(Paper, null, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading || isMeterRowsLoading
  }), /*#__PURE__*/React.createElement(SummaryInfo, {
    allocationMethod: utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.allocationMethod,
    providerName: (_store$utilityProvide = store.utilityProviders.utilities.get(utility.utilityId)) === null || _store$utilityProvide === void 0 ? void 0 : _store$utilityProvide.providerName,
    showRecovery: true,
    summaryData: {
      'Allocated:': "$".concat((utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.allocated) || 0),
      'Bill total:': "$".concat((utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.billTotal) || 0),
      'Deductions:': "".concat((utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.deductions) || 0, "%"),
      'Recovery:': "".concat((utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.recovery) || 0, "%")
    },
    utilityType: utility.utilityTypeName
  }), showsMeterRows ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.title,
    style: {
      padding: '24px 32px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('allocations.submeter_rows.title'))), meterRowsFailed && /*#__PURE__*/React.createElement(SubmeterMeterRowsError, null), meterRows && /*#__PURE__*/React.createElement(SubmeterMeterRows, {
    rows: meterRows
  })) : /*#__PURE__*/React.createElement(Accordion, {
    className: classes.detailsTable,
    expanded: isExpanded
  }, /*#__PURE__*/React.createElement(AccordionSummary, {
    "aria-controls": "allocation-details",
    expandIcon: /*#__PURE__*/React.createElement(Icon, {
      fontSize: "large",
      type: "arrow-line-right"
    }),
    id: "allocation-details",
    onClick: () => setIsExpanded(prevValue => !prevValue)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.title
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('allocations.view_ratio_billed_units_table')), /*#__PURE__*/React.createElement("div", {
    className: classes.totalSummary
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    color: "light"
  }, t('allocations.ratio_billed_total'), "\xA0"), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, "$", (utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.ratioBilledTotal) || 0)))), /*#__PURE__*/React.createElement(AccordionDetails, null, /*#__PURE__*/React.createElement(TableModal, {
    allocations: (utilityTable === null || utilityTable === void 0 ? void 0 : utilityTable.allocations) || []
  })))));
};
export default AllocationPreviewModal;